import React, { useState, useEffect } from 'react';
import { Gift, Users, CheckCircle, Phone, BadgePercent, Calendar, IndianRupee, Clock, Home } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { publicAPI } from '../services/api';
import { companyInfo } from '../data/mock';

const LoyaltyProgramPage = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await publicAPI.getLoyaltyProgram();
        setProgram(response.data);
      } catch (error) {
        console.error('Error fetching loyalty program:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Loyalty Program</h1>
            <p className="text-lg text-white/90 max-w-4xl mx-auto">
              {program?.intro_text || "We have been committed to delivering quality services for over 32 years and recognize that this journey wouldn't have been possible without your support. To extend our appreciation and provide additional benefits, we are thrilled to introduce a loyalty program for the esteemed customers of Nanda Diagnostics. This program is designed to grant access to a host of exclusive advantages."}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-gray-600 text-center max-w-4xl mx-auto text-lg">
            At Nanda Diagnostics, we offer membership program tailored for our loyal clients and their families. You can purchase membership card directly from our counter and enjoy year-round discounts and other exclusive privileges.
          </p>
          <p className="text-amber-600 text-center mt-4 font-medium bg-amber-50 rounded-lg py-3 max-w-2xl mx-auto">
            These cards can only be bought in conjugation with other paid services and activate after 10 days.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Card Info */}
            <div>
              <h5 className="text-[#007897] font-medium mb-2 text-center">Membership Card</h5>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">{program?.title || 'Membership Card'}</h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-center">
                {program?.description || "An exclusive membership card offering recurring benefits on all our services. It is of immense use for patients with chronic diseases (like diabetes, hypertension, etc.) with expected investigation expenditure. With the added benefit of covering your family up to 6 members, it is a lucrative investment to ensure the good health of your family with substantial savings."}
              </p>
              
              {/* Benefits - Now First */}
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Benefits:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-gray-700">
                      <strong>Free Preventive Health Checkup</strong> worth Rs. {program?.free_checkup_value?.toLocaleString() || '1,170'}/-
                      <div className="mt-2 text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                        <span className="font-medium text-green-700">Includes:</span>
                        <ul className="mt-1 space-y-1">
                          <li>• Complete Blood Count (CBC)</li>
                          <li>• Blood Sugar</li>
                          <li>• S. Creatinine</li>
                          <li>• S. Cholesterol</li>
                          <li>• SGPT (ALT)</li>
                          <li>• Thyroid Stimulating Hormone (TSH)</li>
                        </ul>
                        <p className="mt-2 text-green-800 font-medium italic">* For any 1 member. To be availed within 30 days only.</p>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <BadgePercent className="w-5 h-5 text-blue-600 mt-0.5" />
                    <span className="text-gray-700"><strong>10% Discount</strong> on Ultrasound, Specialized Lab Services, Fibrotouch, Echo, TMT, Holter, PFT, EEG, NCV, EMG</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <BadgePercent className="w-5 h-5 text-purple-600 mt-0.5" />
                    <span className="text-gray-700"><strong>20% Discount</strong> on Inhouse Laboratory, Digital X-Ray, OPG, Mammography, Bone Densitometry, ECG</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Home className="w-5 h-5 text-amber-600 mt-0.5" />
                    <span className="text-gray-700"><strong>Home Collection</strong> facility available</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Now Below */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Features:</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-[#007897] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Validity</h3>
              <p className="text-2xl font-bold text-[#007897]">{program?.validity || '5 years'}</p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-[#007897] rounded-xl flex items-center justify-center mx-auto mb-4">
                <IndianRupee className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Cost</h3>
              <p className="text-2xl font-bold text-[#007897]">Rs. {program?.price?.toLocaleString() || '1,000'}/-</p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-[#007897] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Family Members</h3>
              <p className="text-2xl font-bold text-[#007897]">{program?.family_members || 6}</p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-[#007897] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Activation</h3>
              <p className="text-gray-600">After 10 days, Valid from 2nd visit</p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6 text-center md:col-span-2 lg:col-span-2">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Terms & Conditions</h3>
              <p className="text-gray-600 text-sm">Card facility can be withdrawn without prior notice. Standard prices are subject to change without prior notice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#007897] to-[#005a73]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Get Your Membership Card Today</h2>
          <p className="text-white/80 mb-8">
            Purchase directly from our counter and start enjoying exclusive benefits on all our services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#007897] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              <Phone size={20} />
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LoyaltyProgramPage;
